// ProjectMasterView.jsx — rebuilt to match salesgro_crm_v8.html design system
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useInventory } from '../../../contexts/InventoryContext';
import ProjectHeader from './project_master/ProjectHeader';
import ProjectStatsBar from './project_master/ProjectStatsBar';
import ConfigurationsSidebar from './project_master/ConfigurationsSidebar';
import UnitsTable from './project_master/UnitsTable';
import AddNewConfiguration from './AddNewConfiguration';
import AddNewUnit from './AddNewUnit';
import ImportCSVModal from './project_master/ImportCSVModal';

// ── Design tokens mirrored from HTML ------------------------------------------
// Font: Plus Jakarta Sans is loaded in global CSS/index.html
// These match var(--g*), var(--mint*), var(--border*), var(--surface*) from HTML
const T = {
  g900: '#0d2b1a', g800: '#1a4731', g700: '#1e5438', g600: '#246040',
  g500: '#2d7a52', g400: '#3d9466', g300: '#5db583', g200: '#9fd4b5',
  g100: '#d4edd9', g50:  '#eef7f1',
  mint: '#00c46d', mintGlow: 'rgba(0,196,109,.18)', mintSoft: 'rgba(0,196,109,.09)',
  ink:  '#0f1f15', ink2: '#1e3a28', muted: '#506459',
  subtle: '#7a9585', ghost: '#b0c4b5',
  border: '#e0ece4', border2: '#c8ddd2', border3: '#a8c7b8',
  surface: '#f4f8f5', surface2: '#ebf4ee', white: '#ffffff',
};

export default function ProjectMasterView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const {
    getProjectDetail,
    getProjectInventorySummary,
    getConfigsByProjectId,
    fetchUnitsByConfiguration,
    createConfiguration,
    createUnit,
    updateUnit,
    inventoryOptions,
    fetchUnitUiSchema,
  } = useInventory();

  const [project, setProject] = useState(null);
  const [summary, setSummary] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [units, setUnits] = useState([]);
  const [loadingProject, setLoadingProject] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priceSort, setPriceSort] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [addConfigDrawerOpen, setAddConfigDrawerOpen] = useState(false);
  const [addUnitDrawerOpen, setAddUnitDrawerOpen] = useState(false);
  const [unitDrawerUiSchema, setUnitDrawerUiSchema] = useState(null);
  const [importCSVOpen, setImportCSVOpen] = useState(false);

  useEffect(() => {
    if (!addUnitDrawerOpen || !fetchUnitUiSchema) {
      setUnitDrawerUiSchema(null);
      return;
    }
    const config = selectedConfig || {};
    const propertyTypeId =
      config.property_type_id ??
      (typeof config.property_type === 'object' ? config.property_type?.id : config.property_type);
    const categoryId =
      config.category_id ?? (typeof config.category === 'object' ? config.category?.id : config.category);
    fetchUnitUiSchema({ property_type_id: propertyTypeId, category_id: categoryId })
      .then((data) => setUnitDrawerUiSchema(data?.unit_schema ?? null))
      .catch(() => setUnitDrawerUiSchema(null));
  }, [addUnitDrawerOpen, fetchUnitUiSchema, selectedConfig?.id]);

  // ── Data loaders ─────────────────────────────────────────────────────────────
  const loadProjectAndConfigs = useCallback(() => {
    if (!projectId) return;
    setLoadingProject(true);
    Promise.all([getProjectDetail(projectId), getConfigsByProjectId(projectId)])
      .then(([proj, cfgs]) => {
        setProject(proj ?? null);
        const list = Array.isArray(cfgs) ? cfgs : [];
        setConfigs(list);
        if (!selectedConfig && list.length) setSelectedConfig(list[0]);
      })
      .catch(() => { setProject(null); setConfigs([]); })
      .finally(() => setLoadingProject(false));
  }, [projectId, getProjectDetail, getConfigsByProjectId]);

  const loadSummary = useCallback(() => {
    if (!projectId) return;
    setLoadingSummary(true);
    getProjectInventorySummary(projectId)
      .then(setSummary)
      .catch(() => setSummary({ total: 0, available: 0, booked: 0, sold: 0, blocked: 0, not_available: 0 }))
      .finally(() => setLoadingSummary(false));
  }, [projectId, getProjectInventorySummary]);

  const loadUnits = useCallback(() => {
    if (!projectId || !selectedConfig?.id) { setUnits([]); return; }
    setLoadingUnits(true);
    fetchUnitsByConfiguration(projectId, selectedConfig.id)
      .then((list) => setUnits(Array.isArray(list) ? list : []))
      .catch(() => setUnits([]))
      .finally(() => setLoadingUnits(false));
  }, [projectId, selectedConfig?.id, fetchUnitsByConfiguration]);

  useEffect(() => { loadProjectAndConfigs(); }, [loadProjectAndConfigs]);
  useEffect(() => { loadSummary();           }, [loadSummary]);
  useEffect(() => { loadUnits();             }, [loadUnits]);
  useEffect(() => {
    setStatusFilter('all'); setPriceSort(''); setSearchQuery('');
  }, [selectedConfig?.id]);

  // ── Submit handlers ───────────────────────────────────────────────────────────
  const categoryOptions = inventoryOptions?.categories?.length
    ? inventoryOptions.categories
    : [
        { value: 'residential', label: 'Residential' },
        { value: 'commercial',  label: 'Commercial'  },
        { value: 'office',      label: 'Office'      },
        { value: 'warehouse',   label: 'Warehouse'   },
        { value: 'plot',        label: 'Plot'        },
        { value: 'farmhouse',   label: 'Farmhouse'   },
        { value: 'farm_land',   label: 'Farm Land'   },
        { value: 'other',       label: 'Other'       },
      ];

  const handleAddConfigSubmit = async (payload) => {
    if (!projectId || !project?.id) return;
    // category/property_type: id (number) when selected from list, else name as typed (string) — backend stores slug + name
    const category = typeof payload.category_id === 'number' ? payload.category_id : (payload.category_name || payload.category_id || 'residential');
    const propertyType = typeof payload.property_type_id === 'number' ? payload.property_type_id : (payload.property_type_name || payload.property_type_id);
    await createConfiguration(projectId, {
      category,
      property_type: propertyType,
      name: payload.name || '',
      description: payload.description ?? '',
      price_min: payload.min_price ?? null,
      price_max: payload.max_price ?? null,
      area_min: payload.area_min ?? null,
      area_max: payload.area_max ?? null,
      area_unit: payload.area_unit || null,
      commission_rate: payload.commission_rate ?? null,
      tags: payload.tags || [],
      key_highlights: payload.key_highlights || [],
      ...(payload.metadata && Object.keys(payload.metadata).length > 0 ? { metadata: payload.metadata } : {}),
      ...(Array.isArray(payload.pricing) && payload.pricing.length > 0 ? { pricing: payload.pricing } : {}),
    });
    const cfgs = await getConfigsByProjectId(projectId);
    setConfigs(Array.isArray(cfgs) ? cfgs : []);
    setAddConfigDrawerOpen(false);
  };

  const handleAddUnitSubmit = async (apiPayload) => {
    const configId = selectedConfig?.id;
    if (!projectId || !configId) return;
    await createUnit(projectId, configId, apiPayload);
    loadUnits();
    loadSummary();
    setAddUnitDrawerOpen(false);
  };

  const handleUnitStatusChange = async (unit, newStatus) => {
    if (!projectId || !selectedConfig?.id) return;
    try {
      await updateUnit(projectId, selectedConfig.id, unit.id, { inventory_status: newStatus });
      setUnits((prev) => prev.map((u) => u.id === unit.id ? { ...u, inventory_status: newStatus } : u));
      loadSummary();
    } catch (_) {}
  };

  const handleCSVImport = async (apiPayloads) => {
    const configId = selectedConfig?.id;
    const projectIdNum = project?.id;
    if (!configId || !projectIdNum) return;
    for (const payload of apiPayloads) {
      try {
        await createUnit(projectIdNum, configId, payload);
      } catch (_) {}
    }
    loadUnits();
    loadSummary();
    setImportCSVOpen(false);
  };

  // ── Loading & error states (render from top, not centered) ────────────────────
  if (loadingProject && !project) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
        minHeight: '100%', background: T.surface, pt: 3, px: 3,
      }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={28} sx={{ color: T.mint }} />
          <Typography sx={{ fontSize: 12, color: T.subtle, fontWeight: 500 }}>
            Loading project…
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ p: 3, background: T.surface, minHeight: '100vh' }}>
        <Typography sx={{ color: T.subtle, fontSize: 13 }}>Project not found.</Typography>
        <Box
          onClick={() => navigate('/inventory/projects')}
          sx={{
            mt: 2, display: 'inline-flex', alignItems: 'center', gap: 0.75,
            cursor: 'pointer', color: T.g500, fontSize: 13, fontWeight: 600,
            '&:hover': { color: T.g700 },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} /> Back to Projects
        </Box>
      </Box>
    );
  }

  const projectWithPriority = {
    ...project,
    priority: project.metadata?.sales_priority ?? project.priority,
  };

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      minHeight: '100%',
      background: T.surface,
    }}>
      {/* ── Page Header ── */}
      <Box sx={{ px: '24px', pt: '20px', pb: '10px', flexShrink: 0, background: T.white, borderBottom: `1px solid ${T.border}` }}>
        {/* Back link */}
        <Box
          onClick={() => navigate('/inventory/projects')}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            mb: 1.5, cursor: 'pointer',
            color: T.subtle, fontSize: 12, fontWeight: 500,
            transition: 'color 150ms',
            '&:hover': { color: T.g600 },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 13 }} />
          <span>Projects</span>
        </Box>

        <ProjectHeader
          project={projectWithPriority}
          onAddConfig={() => setAddConfigDrawerOpen(true)}
        />
      </Box>

      {/* ── Inventory stats strip ── */}
      <Box sx={{ px: '24px', pt: '12px', pb: 0, flexShrink: 0, background: T.white, borderBottom: `1px solid ${T.border}` }}>
        <ProjectStatsBar
          summary={summary}
          loading={loadingSummary}
          onFilterChange={setStatusFilter}
          activeFilter={statusFilter}
        />
      </Box>

      {/* ── Split: Configs sidebar + Units panel ── */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Left: configurations sidebar */}
        <ConfigurationsSidebar
          configs={configs}
          selectedConfig={selectedConfig}
          onSelectConfig={setSelectedConfig}
          onAddConfig={() => setAddConfigDrawerOpen(true)}
          categoryOptions={inventoryOptions?.categories ?? []}
        />
          <UnitsTable
            projectId={projectId}
            configurationId={selectedConfig?.id}
            config={selectedConfig}
            project={project}
            units={units}
            loading={loadingUnits}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            priceSort={priceSort}
            onPriceSortChange={setPriceSort}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            onImportCSV={() => setImportCSVOpen(true)}
            onAddUnit={() => setAddUnitDrawerOpen(true)}
            onUnitStatusChange={handleUnitStatusChange}
            unitStatusOptions={inventoryOptions?.unit_statuses ?? []}
          />
      </Box>

      {/* ── Drawers ── */}
      <AddNewConfiguration
        open={addConfigDrawerOpen}
        onClose={() => setAddConfigDrawerOpen(false)}
        projectName={project?.name}
        projectCommissionRate={project?.commissionDefault ?? project?.commission_rate ?? project?.commission}
        projectPriceMin={project?.price_min != null ? Number(project.price_min) : undefined}
        projectPriceMax={project?.price_max != null ? Number(project.price_max) : undefined}
        categoryOptions={categoryOptions}
        areaUnitOptions={inventoryOptions?.area_units ?? []}
        propertyTypeOptions={inventoryOptions?.property_types ?? []}
        onSubmit={handleAddConfigSubmit}
      />

      <AddNewUnit
        open={addUnitDrawerOpen}
        onClose={() => setAddUnitDrawerOpen(false)}
        config={selectedConfig || {}}
        project={project || {}}
        projectId={projectId}
        configurationId={selectedConfig?.id}
        propertyTypes={inventoryOptions?.property_types ?? []}
        uiSchema={unitDrawerUiSchema}
        unitStatusOptions={inventoryOptions?.unit_statuses ?? []}
        facingOptions={inventoryOptions?.facing_directions ?? []}
        areaUnitOptions={inventoryOptions?.area_units ?? []}
        onSubmit={handleAddUnitSubmit}
      />

      <ImportCSVModal
        open={importCSVOpen}
        onClose={() => setImportCSVOpen(false)}
        onImport={handleCSVImport}
        projectName={project?.name}
        configurationId={selectedConfig?.id}
        configurationName={selectedConfig?.name}
        areaUnit={selectedConfig?.area_unit || 'sqft'}
      />
    </Box>
  );
}
